// Shared type definitions used by both the client (src/) and server (functions/).
// Keep this file framework-agnostic — no React, no Cloudflare Workers types.

export type DoseForm = 'tablet_7oh' | 'leaf' | 'extract';
export type AmountUnit = 'mg' | 'g' | 'mL';

export interface Dose {
  id: number;
  ts: string; // ISO8601 UTC
  form: DoseForm;
  amount: number;
  amountUnit: AmountUnit;
  concentrationMgPerMl: number | null;
  strain: string | null;
  computedMg7oh: number;
  note: string | null;
  createdAt: string;
  // Powder-tracking mini-sprint (migration 0080). Both nullable for
  // historical rows. vendor + batch are leaf-only inputs in the UI;
  // estimated flips to true when the dose's mg is a personal guess
  // rather than a label-precise reading.
  vendor?: string | null;
  batch?: string | null;
  estimated?: boolean;
  // Custom product registry (migration 0081). Soft pointer to the
  // user_products row this dose was logged from; null for ad-hoc
  // doses. Surfaces in History so the user can later filter "all
  // Zana Chill doses" without grepping the freeform note field.
  productId?: number | null;
  // MEQ tracking surface (migration 0082, Gap 6). Mitragynine-equivalent
  // in mg, persisted per-dose. Null for historical doses (no backfill)
  // and for extracts without a concentration. Used by the optional MEQ
  // stat card in Insights / Today for users who prefer to track in the
  // parent alkaloid unit rather than the 7-OH metabolite.
  computedMgMitragynine?: number | null;
}

// Custom product registry (Gap 2, migration 0081). A user-defined
// shortcut for "this is something I take regularly." Combines a
// label, a default dose shape, and an optional mg-per-unit override
// for products without a published mg/mL or mg/g. The LogDoseSheet
// quick-pick row uses these for one-tap logging.
export interface UserProduct {
  id: number;
  label: string;
  form: DoseForm;
  amount: number;
  amountUnit: AmountUnit;
  // For extract form with a published concentration; null when the
  // mg-per-unit override is used instead.
  concentrationMgPerMl: number | null;
  // Override path: when set, `dose.computedMg7oh = amount * mgPerUnit`,
  // regardless of form. Used for products without a published mg/mL
  // (Zana Chill et al.) and for per-strain leaf calibration.
  mgPerUnit: number | null;
  // True when the mg attached to this product is a personal estimate.
  // Propagates onto every dose logged from this product.
  estimated: boolean;
  notes: string | null;
  archivedAt: string | null;
  createdAt: string;
}

export type Bowel = 'normal' | 'constipated' | 'loose';

export interface SowsAnswers {
  // 16 items, each 0–4. Verbatim from the SOWS instrument.
  q1: number; // anxious
  q2: number; // yawning
  q3: number; // perspiring
  q4: number; // eyes tearing
  q5: number; // nose running
  q6: number; // goose flesh
  q7: number; // shaking
  q8: number; // hot flashes
  q9: number; // cold flashes
  q10: number; // bones and muscles ache
  q11: number; // restless
  q12: number; // nauseous
  q13: number; // feel like vomiting
  q14: number; // muscles twitch
  q15: number; // stomach cramps
  q16: number; // cravings ("feel like using")
}

export interface DailyLog {
  id?: number;
  date: string; // YYYY-MM-DD
  sows: SowsAnswers;
  sowsTotal: number;
  cravings: number; // 0..10
  phq2Q1: number; // 0..3
  phq2Q2: number; // 0..3
  sleepHours: number; // 0..14
  sleepQuality: number; // 1..10
  bowel: Bowel;
  medsTaken: string[];
  note: string | null;
  suicidalIdeation: 0 | 1;
  crisisResourcesShown?: 0 | 1;
  // C-SSRS Screen Version (Phase 20). 0–5 ideation ladder + 0|1
  // behavior flag. Optional on the wire for backward compat with
  // pre-Phase-20 rows. Surfaced in shared/types.ts (Part 12 C) so the
  // Today screen's 72h-crisis predicate doesn't have to widen the
  // type locally — closes the silent-regression hole the audit flagged.
  ssrsIdeationLevel?: number;
  ssrsBehavior?: 0 | 1;
  createdAt?: string;
  // Phase D.2 (§P10.9): which check-in flow produced this row.
  // 'full' = 6-page CheckInScreen, 'minimum' = 2-page MinimumCheckin.
  // Optional client-side; DB defaults to 'full' for backward compat.
  checkinKind?: 'full' | 'minimum';
}

export interface Med {
  id: number;
  name: string;
  doseMg: number | null;
  schedule: string | null;
  active: 0 | 1;
  createdAt: string;
}

export interface LeaderboardOptIn {
  reduction: boolean;
  streak: boolean;
  absolute: boolean;
}

export interface UserSettings {
  timezone?: string;
  notifyMorning?: boolean;
  theme?: 'system' | 'light' | 'dark';
  reducedMotion?: 'system' | 'reduce';
  textSize?: 'default' | 'large';
  motivationalPopups?: boolean;
  quietHoursStart?: string;
  quietHoursEnd?: string;
  notificationsPerDay?: number;
  costPerMg?: number;
  leaderboardOptIn?: LeaderboardOptIn;
  /**
   * Phase D.1 §P10.9 — Rough-Night soften toggle. ISO timestamp. While
   * now < roughNightUntil, the Today screen dims celebratory surfaces
   * (consistency pulse, money/hours saved), swaps the headline to a
   * non-measuring tone, and suppresses motivational toasts. Support
   * surfaces (Urge-surf, Breathe, Crisis) are NEVER gated by this.
   * Null/absent → inactive.
   */
  roughNightUntil?: string | null;
  /**
   * Part 12 §P12.6 C2 — Minimalist Today (Beta). When true, TodayScreen
   * short-circuits to a single "next dose" surface: target mg + a clock
   * showing when the next scheduled dose lands. Crisis link + log-dose
   * action remain available. Default false. Reversible via Settings.
   */
  minimalistTodayEnabled?: boolean;
  /**
   * Part 12 §P12.7 D4 — CrisisBanner snooze. ISO timestamp. While
   * now < crisisBannerSnoozedUntil the banner stays hidden on Today.
   * The 72-h crisis-window predicate still recomputes each render, so
   * if the predicate is still true after the snooze expires the
   * banner re-appears. Replaces the previous "Hide" affordance which
   * collapsed the banner indefinitely once tapped.
   */
  crisisBannerSnoozedUntil?: string | null;
  /**
   * PR 2C (Gap 9) — optional 2-letter US state code (e.g. "CA").
   * Drives the RelapsePreventionCard's state-specific naloxone locator
   * and Good Samaritan law section. Empty string or undefined → no
   * state filtering ("prefer not to say"). Never sent to AI prompts.
   */
  state?: string | null;
  /**
   * PR 3C (Gap 7) — opt-in for proactive nudges 30 min before the
   * user's top-2 historical "danger windows" (craving heatmap). Default
   * OFF; toggled in Insights.
   */
  proactiveNudgesEnabled?: boolean;
  /**
   * Phase 5 PR-A §B4 — hydration nudges for the acute taper weeks.
   * `null` (or absent) → default-on during the user's first 14 days of
   * taper, default-off after week 2. `true` → explicit opt-in for any
   * week. `false` → explicit opt-out. The /api/_cron/hydration cron
   * gates push fan-out on this flag.
   */
  hydrationNudgesEnabled?: boolean | null;
  /**
   * Phase 5 PR-A §B4 — ISO timestamp of the most-recent hydration push.
   * Owned by /api/_cron/hydration; used to enforce a 3.5-hour cooldown
   * so the 3x-daily schedule never double-fires for the same window.
   */
  hydrationLastSentAt?: string | null;
  /**
   * Phase 6 — Chat Buddy session memory. Default OFF (false / undefined).
   * When true, the buddy chat endpoint feeds a one-sentence summary of
   * the user's most recent ended session into the new session's prompt.
   * Recovery-context rule: opt-in only — the bot must not become a
   * parasocial substitute.
   */
  buddyMemoryEnabled?: boolean;
  /**
   * Phase 8 — hide the SOS toolkit card from Today. Default OFF (false /
   * undefined): the card is shown. Setting this to true hides the card
   * for users who don't want it taking up real estate on Today (the
   * underlying toolkit is still reachable via the SOS history surface,
   * but the front-and-center card disappears).
   */
  sosCardHidden?: boolean;
  /**
   * Phase 10A — per-channel push-notification preferences. All default
   * to ON (legacy behavior). The push send path checks this object
   * before dispatching; crisis pushes go regardless of the stored
   * value but the UI tracks user intent here for visibility.
   */
  notificationChannels?: {
    motivational?: boolean;
    milestone?: boolean;
    partner?: boolean;
    crisis?: boolean;
  };
  /**
   * Phase 10A — master "important only" switch. When true, only Partner
   * and Crisis pushes go through. A single toggle for "I need quiet now."
   */
  onlyImportantNotifications?: boolean;
  /**
   * Phase 10A — per-feature data-collection opt-outs. All default ON.
   * When false, the corresponding collector endpoint short-circuits with
   * 204 No Content and writes nothing. No retroactive delete — use the
   * existing export + delete-account flows to remove already-stored rows.
   */
  collectCravingLogs?: boolean;
  collectVoiceJournal?: boolean;
  /**
   * Phase 27 — explicit opt-in to "reduce data" mode. When true (or
   * when navigator.connection.saveData is true), the SPA gates
   * non-safety-critical fetches on user action rather than firing
   * them on every Today-screen mount. Safety surfaces (doses, taper,
   * crisis windows, clinical recommendation) always load eagerly.
   * Default undefined → follow the OS / browser Save-Data hint;
   * false → ignore the OS hint and load everything; true → reduce
   * even without the OS hint. See useSaveDataMode().
   */
  reduceDataMode?: boolean;
  /**
   * User-tunable conversion for kratom leaf/powder → 7-OH mg.
   * Default (and historical value) is the constant MG_7OH_PER_GRAM_LEAF
   * = 0.4 mg/g in shared/constants.ts. Real leaf varies widely by
   * strain (red vs white vs green vein), origin (Indonesia vs
   * Malaysia), and batch freshness — published literature reports
   * direct 7-OH content of 0.03–0.4 mg/g, and functional 7-OH
   * equivalence (accounting for in-vivo mitragynine → 7-OH conversion
   * via CYP3A4) is multiples higher. Setting this lets a user
   * calibrate their tracking honestly for THEIR powder, especially
   * during a transition off pure 7-OH tablets where the default
   * undercounts felt opioid load. Both the LogDoseSheet preview and
   * the server-side POST /api/doses computeMg7oh use this when set;
   * the constant falls through when undefined. Range: 0.05–5.0 mg/g
   * (defends against silly values; the upper bound is generous to
   * allow for users tracking on a "functional MEQ" basis).
   */
  leafPotencyMgPerGram?: number;
  /**
   * User-tunable conversion for kratom leaf/powder → mg of mitragynine.
   * Default (when undefined) falls through to MG_MITRAGYNINE_PER_GRAM_LEAF
   * = 14 mg/g in shared/constants.ts. Used by the optional MEQ
   * (mitragynine-equivalent) tracking surface for users who prefer to
   * see their daily total in mg of the parent alkaloid rather than its
   * 7-OH metabolite. Range: 1-30 mg/g (bounded by realistic literature
   * extremes; 1% leaf is the low end, 3% is the documented high end).
   * If you don't care about MEQ, ignore this — the primary unit stays
   * mg of 7-OH for everyone.
   */
  mitragyninePerGramLeaf?: number;
}

/**
 * Subset of feature flags resolved server-side at bootstrap and shipped
 * down in /api/auth/me so the SPA can branch its render path without
 * a follow-up roundtrip. Keep this list narrow — only flags whose
 * resolution materially changes which surface the user sees belong here.
 * Part 12 §P12.8 E1.
 */
export interface ClientFlags {
  honest_day_minimum_checkin?: boolean;
  /**
   * Phase 3 #15 — when true, OnboardingScreen offers a "Just log for
   * now" branch after the disclaimer. The branch sets a localStorage
   * marker (`qk.deferredBaseline.v1 = '1'`) and routes to /app where
   * quick-log chips appear inside the FirstRunGuide. The user is
   * nudged to complete baseline + plan-shape after their first dose.
   * When false, the SPA uses the legacy strict-gated 5-step flow.
   */
  log_first_onboarding?: boolean;
  /**
   * Phase 11 Wave 2 — when true, the Plus paywall surface is live: the
   * Settings screen reveals an "Upgrade to Plus" entry and the
   * `/app/upgrade` screen offers checkout. When false (the default
   * until billing is switched on), the Upgrade surface stays invisible
   * even though the route exists. Mirrors the server-side billing
   * kill-switch so the UI never offers checkout against a disabled API.
   */
  billing_enabled?: boolean;
}

/**
 * Phase 11 — subscription tier. Every user is 'free' until billing
 * ships (Wave 2); 'plus' is the paid consumer tier.
 */
export type SubscriptionTier = 'free' | 'plus';

export interface AuthMe {
  email: string;
  displayName: string | null;
  settings: UserSettings;
  baselineLocked: boolean;
  /** Owner / admin role — gates `/app/admin/*` in the SPA. */
  isAdmin: boolean;
  /**
   * Phase 14 §S1 — true when the user has confirmed ownership of their
   * email via the verify-email link. Pre-S1 accounts are grandfathered
   * to true by the 0031 migration; new registrations start at false
   * until they hit /api/auth/verify-email. The SPA gates the app shell
   * on this — when false, the user is redirected to
   * VerifyEmailNeededScreen with only legal pages + logout available.
   * Optional on the wire so a server with stale schema (column not
   * applied yet) doesn't break client typing; treat undefined as true
   * (graceful degradation) for backward compat with the pre-S1 shape.
   */
  emailVerified?: boolean;
  /** Server-resolved client-visible feature flags. See ClientFlags. */
  flags?: ClientFlags;
  /**
   * Phase 11 Wave 1 — the user's subscription tier. 'free' for everyone
   * until billing (Wave 2) ships. Optional on the wire for graceful
   * degradation during the deploy window; treat undefined as 'free'.
   */
  tier?: SubscriptionTier;
}

export interface LeaderboardRowWire {
  rank: number;
  pseudonym: string;
  value: number;
  isYou: boolean;
}

export type LeaderboardTab = 'reduction' | 'streak' | 'absolute';
export type LeaderboardWindow = '24h' | '7d';

export interface LeaderboardResponse {
  tab: LeaderboardTab;
  window: LeaderboardWindow;
  rows: LeaderboardRowWire[];
  yourRow: LeaderboardRowWire | null;
  updatedAt: string | null;
}

export interface StreakInfo {
  currentStreakDays: number;
  longestStreakDays: number;
  freezesRemaining: number;
  lastEvaluatedAt: string | null;
  /** 0–100 habit-strength curve (Phase 18; Loop Habit Tracker model). */
  consistencyScore: number;
  /** Last 30 days of consistency snapshots. */
  consistencyHistory: { date: string; score: number }[];
  /**
   * PR 1B (Gap 2) — completed stretches between resets. The currently-
   * running streak is NOT in here; see `currentStreakDays`. Each entry
   * records the inclusive date range + days length + a break-reason
   * label so the UI can be empathetic about how the streak ended.
   */
  stretches: StretchWire[];
  /**
   * Mean length of `stretches`, rounded to one decimal. 0 when the user
   * has never completed a stretch yet (first-week users). Computed
   * server-side from stretches so the client never has to.
   */
  averageStretchDays: number;
}

export interface StretchWire {
  from: string; // YYYY-MM-DD
  to: string; // YYYY-MM-DD
  days: number;
  breakReason: 'over_target' | 'missed_log';
}

// Part F9 — per-day cumulative point shipped to the Insights sparkline.
// Cumulative so the line is monotonic ascending ("this number only goes
// up"). Capped server-side at ~365 days to keep the payload bounded.
export interface MoneyTimeSeriesPoint {
  date: string; // YYYY-MM-DD
  cumulativeMoneyUsd: number;
  cumulativeTimeMinutes: number;
}

export interface MoneyTimeSaved {
  dollarsSaved: number;
  hoursReclaimed: number;
  baselineMg: number | null;
  totalMgUnder: number;
  daysCounted: number;
  costPerMg: number;
  /**
   * Per-day cumulative savings since baseline lock. Empty when the user
   * hasn't locked a baseline yet. Server caps at ~365 entries.
   */
  series: MoneyTimeSeriesPoint[];
  /**
   * Phase 5 PR-B — recovery-dashboard tiles. All fields are nullable so
   * existing callers that only render the dollars+hours pair keep working
   * unchanged; the RecoveryScreen renders a "Not yet" empty state when a
   * tile arrives null.
   */
  mgUnderBaselineTotal?: number | null;
  doseFreeDaysCount?: number | null;
  longestStreakDays?: number | null;
  sleepHoursRegained?: number | null;
  monthsSinceStart?: number | null;
}

export interface PopupWire {
  id: string;
  category:
    | 'identity'
    | 'progress'
    | 'compassion'
    | 'urge_reminder'
    | 'streak_celebrate'
    | 're_engagement';
  text: string;
}

// Taper plan shape returned by /api/taper
export interface TaperDayWire {
  dayIndex: number;
  date: string;
  targetMg: number;
  isHold: boolean;
  holdReason: string | null;
}

export interface TaperPlanWire {
  planId: string;
  baselineMg: number;
  reductionPerPeriod: number;
  reductionPerWeek: number;
  periodDays: number;
  weeks: number;
  startDate: string;
  endDate: string;
  days: TaperDayWire[];
  warnings: TaperWarningWire[];
}

export type TaperWarningWire =
  | { kind: 'aggressive'; rPerWeek: number; suggestedWeeks: number }
  | { kind: 'cold_turkey'; rPerWeek: number; suggestedWeeks: number }
  | { kind: 'low_baseline'; baselineMg: number }
  | { kind: 'too_long'; weeks: number };

export interface ChatTurn {
  role: 'user' | 'assistant';
  content: string;
}

export interface ApiError {
  error: string;
  issues?: unknown;
}

// Phase C.1 — Quiet Hand-Off. A 280-char operational note from
// yesterday-you to today-you, with a 12/24/72 h TTL. Surfaces on
// the morning Today screen until tapped ("Thanks, past me") or
// expired. See `migrations/0019_quiet_handoff.sql`.
export interface HandoffRow {
  id: number;
  body: string;
  createdAt: string;
  expiresAt: string;
  consumedAt: string | null;
}

// Phase C.2 §P10.7 — Letters to Future You. A user records a short
// audio "letter" to themselves and picks how many days from now it
// should appear on the Today screen. The audio lives in R2; this row
// carries metadata + the Whisper transcript fallback. Delivery is
// flipped by the daily cron (deliver_at <= now → set delivered_at).
//
// Discriminator semantics on the row:
//   - deliveredAt === null                    → 'pending' (in flight)
//   - deliveredAt !== null && dismissedAt === null → 'delivered' (surfacing now)
//   - dismissedAt !== null                    → 'archive' (read and put away)
export type LetterDeliverInDays = 14 | 21 | 30 | 60 | 90;

export interface LetterRow {
  id: number;
  mimeType: string;
  durationSeconds: number;
  transcript: string | null;
  deliverAt: string;
  createdAt: string;
  deliveredAt: string | null;
  dismissedAt: string | null;
}

export interface LettersResponse {
  delivered: LetterRow[];
  pending: LetterRow[];
  archive: LetterRow[];
  count: number;
}

// Phase C.3 — Cohort-of-One Lab. Descriptive comparisons across the
// user's own data; never compared across users. The endpoint gates this
// behind ≥35 daily_logs and per-bucket counts ≥10.
export interface PatternComparison {
  key: string;
  label: string;
  conditionA_label: string;
  conditionB_label: string;
  metric_label: string;
  metric_unit?: string;
  n_A: number;
  n_B: number;
  mean_A: number;
  mean_B: number;
}

export interface PatternsResponse {
  patterns: PatternComparison[];
  daysLogged: number;
  reason?: 'insufficient_data';
}

export const SOWS_QUESTIONS: { key: keyof SowsAnswers; text: string }[] = [
  { key: 'q1', text: 'I feel anxious' },
  { key: 'q2', text: 'I feel like yawning' },
  { key: 'q3', text: "I'm perspiring" },
  { key: 'q4', text: 'My eyes are tearing' },
  { key: 'q5', text: 'My nose is running' },
  { key: 'q6', text: 'I have goose flesh' },
  { key: 'q7', text: 'I am shaking' },
  { key: 'q8', text: 'I have hot flashes' },
  { key: 'q9', text: 'I have cold flashes' },
  { key: 'q10', text: 'My bones and muscles ache' },
  { key: 'q11', text: 'I feel restless' },
  { key: 'q12', text: 'I feel nauseous' },
  { key: 'q13', text: 'I feel like vomiting' },
  { key: 'q14', text: 'My muscles twitch' },
  { key: 'q15', text: 'I have cramps in my stomach' },
  { key: 'q16', text: 'I feel like using' },
];

export const SOWS_LABELS = ['None', 'Mild', 'Moderate', 'Severe', 'Extreme'] as const;

// SOWS subscale groupings — opioid-withdrawal-scale literature
// (COWS shares this structure) clusters the 16 items into four
// physiologically distinct buckets. Total = sum of all four.
//
// - psychological: anxiety, restlessness, craving
// - autonomic:     yawning, sweat, eyes, runny nose, gooseflesh, hot/cold flashes
// - musculoskeletal: shaking, aches, twitch
// - gi:            nausea, vomiting, cramps
//
// Each item is scored 0–4 (SOWS_LABELS). Subscale max = items * 4.
export interface SowsSubscaleScore {
  psychological: number; // 0–12 (3 items)
  autonomic: number; // 0–28 (7 items)
  musculoskeletal: number; // 0–12 (3 items)
  gi: number; // 0–12 (3 items)
}

export const SOWS_SUBSCALES = {
  psychological: ['q1', 'q11', 'q16'],
  autonomic: ['q2', 'q3', 'q4', 'q5', 'q6', 'q8', 'q9'],
  musculoskeletal: ['q7', 'q10', 'q14'],
  gi: ['q12', 'q13', 'q15'],
} as const satisfies Record<string, readonly (keyof SowsAnswers)[]>;

export const SOWS_SUBSCALE_MAX: SowsSubscaleScore = {
  psychological: 12,
  autonomic: 28,
  musculoskeletal: 12,
  gi: 12,
};

export function computeSowsSubscales(answers: SowsAnswers): SowsSubscaleScore {
  const sum = (keys: readonly (keyof SowsAnswers)[]): number =>
    keys.reduce((acc, k) => acc + (answers[k] ?? 0), 0);
  return {
    psychological: sum(SOWS_SUBSCALES.psychological),
    autonomic: sum(SOWS_SUBSCALES.autonomic),
    musculoskeletal: sum(SOWS_SUBSCALES.musculoskeletal),
    gi: sum(SOWS_SUBSCALES.gi),
  };
}

export const PHQ2_QUESTIONS = [
  'Little interest or pleasure in doing things',
  'Feeling down, depressed, or hopeless',
] as const;

export const PHQ2_LABELS = [
  'Not at all',
  'Several days',
  'More than half the days',
  'Nearly every day',
] as const;

// Part 14 §F10 — /api/health response shape (the user-facing variant;
// the existing handler in functions/api/health.ts also returns
// `status` / `ts` / `db` for ops, those fields are ignored client-side).
export interface HealthResponse {
  status: 'ok';
  ts: string;
  version: string;
  db?: 'ok' | 'fail' | 'skipped';
}

// Audit item F8 — finite milestone unlock set surfaced on Today.
// Six keys (streak-7/30/90, reduction-25/50, dose-free) defined in
// src/lib/milestones.ts. Server returns the unlocked list + the
// progress numbers the client uses to render a "next milestone" hint.
export interface MilestoneUnlockWire {
  key: string;
  unlockedAt: number;
}

export interface MilestoneProgressWire {
  /** Current consecutive-day streak from the server's streak evaluation. */
  streakDays: number;
  /** Today's target as a % below baseline. 0 when there is no plan. */
  reductionPct: number;
  /**
   * Whole days since the most-recent logged dose. Null when the user has
   * never logged a dose, or has logged one today (= not dose-free).
   */
  daysSinceLastDose: number | null;
}

export interface MilestonesResponse {
  unlocked: MilestoneUnlockWire[];
  progress: MilestoneProgressWire;
}

// Part 14 §F11 — admin-curated banner.
export type AnnouncementKind = 'info' | 'warn' | 'critical';
export interface Announcement {
  id: number;
  message: string;
  kind: AnnouncementKind;
  startsAt: string;
  endsAt: string | null;
}
export interface AnnouncementResponse {
  announcement: Announcement | null;
}

// PR 1A — "My Whys" identity anchor surface (Gap 1).
//
// 3-7 reasons-for-quitting captured in onboarding, rotated on the Today
// tile, auto-shown when starting an urge-surf session. Soft-archive via
// is_active so a "I changed my mind" deletion isn't truly destructive.
export type WhyCategory =
  | 'money'
  | 'identity'
  | 'kids'
  | 'health'
  | 'hiding'
  | 'time-loss'
  | 'other';

export const WHY_CATEGORIES: ReadonlyArray<WhyCategory> = [
  'money',
  'identity',
  'kids',
  'health',
  'hiding',
  'time-loss',
  'other',
];

export interface WhyRow {
  id: number;
  category: WhyCategory;
  body: string;
  displayOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface WhysResponse {
  whys: WhyRow[];
}

// Recovery Forecast — a daily "weather report" for the Today screen.
// A deterministic fusion of signals the app already collects (withdrawal
// trend, sleep, mood, cravings, PAWS day, an upcoming dose step-down, a
// danger window) into one honest, glanceable read. Scoring logic lives
// in functions-utils/recoveryForecast.ts.
export type RecoveryForecastBand = 'tougher' | 'steady' | 'lighter';

export interface RecoveryForecastTool {
  kind: 'urge_surf' | 'sleep_reset' | 'breathe';
  /** Short button label. */
  label: string;
  /** In-app route the tool opens. */
  route: string;
}

export interface RecoveryForecast {
  band: RecoveryForecastBand;
  /** One-line, plain-language headline. */
  headline: string;
  /** Up to 3 short "why" lines behind the read. */
  reasons: string[];
  /** A danger window still upcoming later today, if any. */
  dangerWindow: { hour: number; label: string } | null;
  /** Coping tools pre-staged for today's signals; may be empty. */
  tools: RecoveryForecastTool[];
  /** True when too little is logged to forecast — the card hides itself. */
  lowData: boolean;
  /** ISO timestamp the forecast was computed. */
  generatedAt: string;
}
